import AgoraRTC, {
  Client,
  MediaDeviceInfo as AgoraMediaDeviceInfo,
} from "agora-rtc-sdk";
import { useEffect, useMemo, useCallback, useRef } from "react";
import { useAtom } from "jotai";
import { useUpdateAtom } from "jotai/utils";
import { useToasts } from "react-toast-notifications";
import { agoraClientAtom, currentSpaceAtom, streamsAtom } from "../jotai/atoms";

// TODO: shared secret with api/agora
// https://console.agora.io/ -> project -> edit -> "App ID:"
const APP_ID = "23fc0724ccf84a6f9b38f5574a30d4a8";

declare global {
  interface Window {
    debug: any;
  }
}

async function requestToken(spaceId: string) {
  const response = await fetch(`/api/agora?channel=${spaceId}`);
  const token = await response.text();
  return token;
}

enum AGORA_LOG_LEVELS {
  DEBUG = 0,
  INFO = 1,
  WARNING = 2,
  ERROR = 3,
  NONE = 4,
}

// parse out user id from stream id.
function parseStreamId(id: string) {
  return id.split("_")[0];
}

// handles joining an agora channel
// makes the agora client object available via the agoraClientAtom
export function useAgoraClient(
  userId: string | undefined,
  spaceId: string | undefined
) {
  const [client, setClient] = useAtom(agoraClientAtom);

  const { addToast } = useToasts();

  useEffect(() => {
    if (!spaceId) {
      return;
    }

    if (!userId) {
      return;
    }

    let client: Client | undefined = undefined;

    const load = async () => {
      AgoraRTC.Logger.setLogLevel(AGORA_LOG_LEVELS.WARNING);

      client = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
      client.init(APP_ID);

      const token = await requestToken(spaceId);
      client.join(
        token,
        spaceId, // append extra string to stream to prevent collisions when people refresh
        userId + "_" + Math.random().toString().slice(2, 10),
        "makespace",
        () => {
          // enabling dual stream blocks switching audio/video devices!
          // https://docs.agora.io/en/Video/API%20Reference/web/interfaces/agorartc.stream.html#switchdevice:
          // > This method does not support the following scenarios:
          // > - Dual-stream mode is enabled
          //
          // if (client) {
          //   client.enableDualStream(
          //     () => console.log("dual stream enabled"),
          //     (error) => {
          //              addToast("Streaming error: " + error);
          //     }
          //   );
          // }

          setClient(client);
        },
        (error) => {
          console.log(error);
          addToast("Error Joining Channel: " + error);
        }
      );
    };

    load();

    return () => {
      if (client) {
        client.leave();
        setClient(undefined);
      }
    };
  }, [userId, spaceId, setClient, addToast]);

  return { client };
}

export function useAgoraControls(currentUserId?: string) {
  const [streams] = useAtom(streamsAtom);
  const [space] = useAtom(currentSpaceAtom);
  const localStream = currentUserId ? streams[currentUserId] : undefined;
  const initializingStream = useRef<Promise<AgoraRTC.Stream>>();
  const { addToast } = useToasts();

  const [client] = useAtom(agoraClientAtom);

  const setStreams = useUpdateAtom(streamsAtom);

  // start or get local stream
  const initStream = useCallback(() => {
    if (!client) {
      return;
    }

    if (!currentUserId) {
      return;
    }

    if (localStream) {
      return new Promise<AgoraRTC.Stream>((resolve) => resolve(localStream));
    }
    if (initializingStream.current) {
      return initializingStream.current;
    }
    const newLocalStream = AgoraRTC.createStream({
      audio: true,
      video: true,
    });

    // for list of video profiles see https://docs.agora.io/en/Video/API%20Reference/web/interfaces/agorartc.stream.html#setvideoprofile
    newLocalStream.setVideoProfile("480p_4");
    let streamInitPromise = new Promise<AgoraRTC.Stream>((resolve) => {
      newLocalStream.init(
        () => {
          // we start by muting both and then immediatly unmuting the one we want.
          // in the future, we might want to skip these lines in certain situations
          // because initStream is always triggered by an unmute button click
          newLocalStream.muteAudio();
          newLocalStream.muteVideo();

          setStreams((streams) => ({
            ...streams,
            [currentUserId]: newLocalStream,
          }));
          client.publish(newLocalStream, (error) => {
            console.error(error);
            addToast("Error publishing stream: " + error);
          });
          resolve(newLocalStream);
        },
        (error) => {
          console.error(error);
          addToast("Error starting stream: " + error.msg);
        }
      );
    });
    initializingStream.current = streamInitPromise;

    return streamInitPromise;
  }, [addToast, client, currentUserId, localStream, setStreams]);

  useEffect(() => {
    if (!client) {
      return;
    }
    if (!localStream) {
      return;
    }
    return () => {
      console.error("unpublishing");
      client.unpublish(localStream);
    };
  }, [client, localStream]);

  // track other streams
  useEffect(() => {
    if (!client) {
      return;
    }

    if (!space?.id) {
      return;
    }

    // we're using both `onStreamAdded` and `onStreamSubscribed`, following examples from Agora, but we're not sure why/if we need both!
    const onStreamAdded = (e: any) => {
      console.log("onStreamAdded", e);
      client.subscribe(e.stream);
    };

    const onStreamSubscribed = (e: any) => {
      console.log("onStreamSubscribed", e);
      //recover user id from stream id
      let parsedId = parseStreamId(e.stream.streamId);
      setStreams((streams) => ({ ...streams, [parsedId]: e.stream }));
    };

    // we're using both `onStreamRemoved` and `onPeerLeave`, following examples from Agora, but we're not sure why/if we need both!
    const onStreamRemoved = (e: any) => {
      console.log("onStreamRemoved", e);
      //recover user id from stream id
      let parsedId = parseStreamId(e.stream.streamId);
      setStreams((streams) => {
        const { [parsedId]: omit, ...remaining } = streams;
        return remaining;
      });
    };

    const onPeerLeave = (e: any) => {
      console.log("onPeerLeave", e);
      onStreamRemoved(e);
    };

    const onTokenPrivilegeWillExpire = async () => {
      console.log("onTokenPrivilegeWillExpire");
      const token = await requestToken(space?.id);
      client.renewToken(token);
    };

    client.on("stream-added", onStreamAdded);
    client.on("stream-subscribed", onStreamSubscribed);
    client.on("stream-removed", onStreamRemoved);
    client.on("stream-unpublished", onStreamRemoved);
    client.on("peer-leave", onPeerLeave);
    client.on("onTokenPrivilegeWillExpire", onTokenPrivilegeWillExpire);

    return () => {
      client.off("stream-added", onStreamAdded);
      client.off("stream-subscribed", onStreamSubscribed);
      client.off("stream-removed", onStreamRemoved);
      client.off("stream-unpublished", onStreamRemoved);
      client.off("peer-leave", onPeerLeave);
      client.off("onTokenPrivilegeWillExpire", onTokenPrivilegeWillExpire);
    };
  }, [client, space?.id, setStreams]);

  const controls = useMemo(() => {
    return {
      stopAudio: () => {
        localStream && localStream.muteAudio();
      },
      startAudio: (cb: Function) => {
        if (!client) {
          return;
        }
        initStream()?.then((stream) => {
          stream?.unmuteAudio();
          cb();
        });
      },
      stopVideo: () => {
        localStream && localStream.muteVideo();
      },
      startVideo: (cb: Function) => {
        if (!client) {
          return;
        }
        initStream()?.then((stream: AgoraRTC.Stream) => {
          stream?.unmuteVideo();
          cb();
        });
      },

      enumerateAudioDevices: (): Promise<AgoraMediaDeviceInfo[]> => {
        return new Promise((resolve) => {
          AgoraRTC.getDevices((devices) => {
            resolve(devices.filter((d) => d.kind === "audioinput"));
          });
        });
      },
      enumerateVideoDevices: (): Promise<AgoraMediaDeviceInfo[]> => {
        return new Promise((resolve) => {
          AgoraRTC.getDevices((devices) => {
            resolve(devices.filter((d) => d.kind === "videoinput"));
          });
        });
      },

      selectAudioInput: (deviceId: string) => {
        return new Promise((resolve, reject) => {
          if (!localStream) {
            return reject(new Error("no local stream"));
          }

          localStream.switchDevice(
            "audio",
            deviceId,
            () => resolve(true),
            reject
          );
        });
      },
      selectVideoInput: (deviceId: string) => {
        return new Promise((resolve, reject) => {
          if (!localStream) {
            return reject(new Error("no local stream"));
          }

          localStream.switchDevice(
            "video",
            deviceId,
            () => resolve(true),
            reject
          );
        });
      },
    };
  }, [localStream, client, initStream]);

  return controls;
}
